/**
 * Tenant utility functions for dynamic tenant extraction and management
 */

export interface TenantInfo {
  subdomain: string
  businessName?: string
  id?: string
}

const DEFAULT_TENANT = 'tbs'
const VALID_SUBDOMAIN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i

/**
 * Coerce a tenant value to a plain subdomain string.
 * Guards against corrupt localStorage/URL values like
 * `{"id":"tbs","subdomain":"tbs","businessName":"tbs"}` which break
 * X-Tenant-Subdomain headers and login UI copy.
 */
export function normalizeTenantSubdomain(raw: unknown, fallback = DEFAULT_TENANT): string {
  if (raw == null) return fallback

  let value = typeof raw === 'string' ? raw.trim() : String(raw).trim()
  if (!value || value === '[object Object]') {
    if (typeof console !== 'undefined' && raw != null) {
      console.warn(
        `[tenant] Invalid tenant value coerced to "${fallback}"`,
        { raw },
      )
    }
    return fallback
  }

  // Accidentally stored JSON.stringify(tenantObject)
  if (value.startsWith('{')) {
    try {
      const parsed = JSON.parse(value) as { subdomain?: unknown; id?: unknown }
      if (typeof parsed?.subdomain === 'string' && parsed.subdomain.trim()) {
        value = parsed.subdomain.trim()
      } else if (
        typeof parsed?.id === 'string' &&
        parsed.id.trim() &&
        VALID_SUBDOMAIN.test(parsed.id.trim())
      ) {
        value = parsed.id.trim()
      }
    } catch {
      // keep original value; validation below may reject it
    }
  }

  // Decode once if URL-encoded JSON leaked into ?tenant=
  try {
    if (/%7b/i.test(value) || /%22/i.test(value)) {
      const decoded = decodeURIComponent(value)
      if (decoded !== value) {
        return normalizeTenantSubdomain(decoded, fallback)
      }
    }
  } catch {
    // ignore decode errors
  }

  if (!VALID_SUBDOMAIN.test(value)) {
    if (typeof console !== 'undefined') {
      console.warn(
        `[tenant] Invalid tenant subdomain "${value}" coerced to "${fallback}"`,
      )
    }
    return fallback
  }

  return value.toLowerCase()
}

function persistNormalizedTenant(subdomain: string): void {
  if (typeof window === 'undefined') return
  localStorage.setItem('currentTenant', subdomain)
  localStorage.setItem('tenantSubdomain', subdomain)
}

/**
 * Extract tenant subdomain from URL parameters
 * Priority order:
 * 1. ?tenant= parameter from current URL
 * 2. Stored tenant from localStorage (for persistence)
 * 3. Fallback to 'tbs' for development
 */
export const getCurrentTenant = (): string => {
  if (typeof window === 'undefined') {
    return 'demo' // Default for server-side
  }

  // PRIORITY 1: URL parameters (highest priority - overrides localStorage)
  // This ensures reset links with ?tenant=tbs work correctly
  const urlParams = new URLSearchParams(window.location.search)
  const urlTenant = urlParams.get('tenant')
  if (urlTenant) {
    const normalized = normalizeTenantSubdomain(urlTenant)
    const currentStored = localStorage.getItem('currentTenant')
    if (currentStored !== normalized) {
      persistNormalizedTenant(normalized)
    }
    // Self-heal corrupt ?tenant= values in the address bar without a full reload loop
    if (urlTenant !== normalized) {
      urlParams.set('tenant', normalized)
      const cleaned = `${window.location.pathname}?${urlParams.toString()}${window.location.hash || ''}`
      window.history.replaceState({}, document.title, cleaned)
    }
    return normalized
  }

  // PRIORITY 2: localStorage persistence
  // Check for both possible keys for backward compatibility
  const storedTenant =
    localStorage.getItem('currentTenant') || localStorage.getItem('tenantSubdomain')
  if (storedTenant) {
    const normalized = normalizeTenantSubdomain(storedTenant)
    if (storedTenant !== normalized) {
      persistNormalizedTenant(normalized)
    }
    return normalized
  }

  // PRIORITY 3: Extract from current URL path (for cases like /login?tenant=dor-spa)
  const pathMatch = window.location.pathname.match(/\/login\?tenant=([^&]+)/)
  if (pathMatch) {
    const pathTenant = normalizeTenantSubdomain(pathMatch[1])
    persistNormalizedTenant(pathTenant)
    return pathTenant
  }

  // PRIORITY 4: Extract from admin URL path only when it looks like /tenant/{subdomain}/...
  // Skip known app routes under /tenant/* (dashboard, settings, staff, …)
  const adminPathMatch = window.location.pathname.match(/^\/tenant\/([^\/]+)/)
  if (adminPathMatch) {
    const segment = adminPathMatch[1].toLowerCase()
    const reservedTenantRoutes = new Set([
      // Keep in sync with apps/frontend_ccr/src/pages/tenant/* top-level segments
      'dashboard',
      'settings',
      'staff',
      'bookings',
      'customers',
      'payments',
      'pos',
      'reports',
      'inventory',
      'vouchers',
      'availability',
      'calendar',
      'email-templates',
      'health-questionnaires',
      'sites',
      'practitioners',
      'treatment-rooms',
      'products',
      'services',
      'packages',
      'sessions',
      'admin',
      'categories',
      'room-types',
      'blocked-accounts',
      'help',
    ])
    if (!reservedTenantRoutes.has(segment)) {
      const pathTenant = normalizeTenantSubdomain(segment, '')
      if (pathTenant) {
        persistNormalizedTenant(pathTenant)
        return pathTenant
      }
    }
  }

  // PRIORITY 5: Extract from referrer if available
  if (document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer)
      const referrerTenant = referrerUrl.searchParams.get('tenant')
      if (referrerTenant) {
        const normalized = normalizeTenantSubdomain(referrerTenant)
        persistNormalizedTenant(normalized)
        return normalized
      }
    } catch {
      // ignore invalid referrer
    }
  }

  // PRIORITY 6: Prefer subdomain from stored tenant object (full JSON under "tenant")
  const tenantJson = localStorage.getItem('tenant')
  if (tenantJson) {
    const fromTenantObject = normalizeTenantSubdomain(tenantJson, '')
    if (fromTenantObject) {
      persistNormalizedTenant(fromTenantObject)
      return fromTenantObject
    }
  }

  // PRIORITY 7: Check stored auth session for tenant hints
  const user = localStorage.getItem('user')
  if (user) {
    try {
      const userData = JSON.parse(user)
      // For development, if user has tenant info, use 'tbs' as fallback
      if (userData.email && userData.email.includes('thermaebathspa.com')) {
        persistNormalizedTenant(DEFAULT_TENANT)
        return DEFAULT_TENANT
      }
    } catch {
      // Invalid JSON, ignore
    }
  }

  // PRIORITY 8: Default fallback - set 'tbs' in localStorage so it's available for API calls
  persistNormalizedTenant(DEFAULT_TENANT)
  return DEFAULT_TENANT
}

/**
 * Set the current tenant in localStorage
 */
export const setCurrentTenant = (tenant: string): void => {
  if (typeof window !== 'undefined') {
    persistNormalizedTenant(normalizeTenantSubdomain(tenant))
  }
}

/**
 * Clear the current tenant from localStorage
 */
export const clearCurrentTenant = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('currentTenant')
    localStorage.removeItem('tenantSubdomain')
  }
}

/**
 * Get tenant headers for API requests
 * Returns the headers needed for tenant-based authentication
 */
export const getTenantHeaders = (): Record<string, string> => {
  const tenant = getCurrentTenant()
  return {
    'X-Tenant-Subdomain': tenant
  }
}

/**
 * Enhanced API request options with tenant headers
 */
export const getTenantRequestOptions = (options: RequestInit = {}): RequestInit => {
  return {
    ...options,
    headers: {
      ...options.headers,
      ...getTenantHeaders()
    }
  }
}

/**
 * Extract tenant from login URL and store it
 * Call this function when user lands on login page
 */
export const extractAndStoreTenantFromLogin = (): string => {
  if (typeof window === 'undefined') {
    return 'demo'
  }

  const urlParams = new URLSearchParams(window.location.search)
  const tenant = urlParams.get('tenant')

  if (tenant) {
    const normalized = normalizeTenantSubdomain(tenant)
    setCurrentTenant(normalized)
    return normalized
  }

  return getCurrentTenant()
}

/**
 * Build a URL (tenant is handled via headers/subdomain, not URL params)
 */
export const buildUrlWithTenant = (baseUrl: string): string => {
  return baseUrl
}

/**
 * Check if tenant context is available
 */
export const hasTenantContext = (): boolean => {
  const tenant = getCurrentTenant()
  return tenant !== null && tenant !== undefined && tenant !== ''
}

/**
 * Tenant-aware navigation helper (tenant is handled via headers/subdomain)
 */
export const navigateWithTenant = (path: string): void => {
  if (typeof window !== 'undefined') {
    window.location.href = path
  }
}

/**
 * Get the current tenant's currency from API or cache
 */
export const getTenantCurrency = async (): Promise<string> => {
  try {
    // Check if we have cached currency data
    const cachedData = localStorage.getItem('tenantCurrency')
    if (cachedData) {
      const { currency, timestamp } = JSON.parse(cachedData)
      // Cache for 1 hour
      if (Date.now() - timestamp < 3600000) {
        return currency
      }
    }

    const { api } = await import('./api');
    const response = await api.publicRequest('/tenants/settings')

    if (!response.ok) {
      throw new Error('Failed to fetch tenant settings')
    }

    const data = await response.json()
    const currency = data.data.settings.tenant.currency || 'USD'

    // Cache the currency data
    localStorage.setItem('tenantCurrency', JSON.stringify({
      currency,
      timestamp: Date.now()
    }))

    return currency
  } catch (error) {
    console.error('Error fetching tenant currency:', error)
    return 'USD' // Default fallback
  }
}

/**
 * Get tenant currency synchronously (from cache or default)
 */
export const getTenantCurrencySync = (): string => {
  // Check if we're on the client side (SSR-safe)
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return 'GBP' // Default fallback for server-side rendering
  }

  try {
    const cachedData = localStorage.getItem('tenantCurrency')
    if (cachedData) {
      const { currency, timestamp } = JSON.parse(cachedData)
      // Cache for 1 hour
      if (Date.now() - timestamp < 3600000) {
        return currency
      }
    }
  } catch (error) {
    console.error('Error reading cached currency:', error)
  }

  return 'GBP' // Default fallback
}

/**
 * Set tenant currency in cache
 */
export const setTenantCurrency = (currency: string): void => {
  // Check if we're on the client side (SSR-safe)
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return
  }

  localStorage.setItem('tenantCurrency', JSON.stringify({
    currency,
    timestamp: Date.now()
  }))
}

/**
 * Clear tenant currency cache (useful after updating settings)
 */
export const clearTenantCurrencyCache = (): void => {
  // Check if we're on the client side (SSR-safe)
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return
  }

  localStorage.removeItem('tenantCurrency')
}
